<template>
  <div>
    <header-component />
    <div class="container-fluid mt-4">
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <h3 class="mb-0">Order List</h3>
            <div class="d-flex align-items-center gap-2">
              <select
                v-model="statusFilter"
                class="form-control form-control-sm"
                style="width:160px;"
                :disabled="!selectedBranchId"
                @change="onStatusChange"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select v-model="selectedBranchId" @change="onBranchChange" class="form-control form-control-sm" style="width:200px;">
                <option value="">Select Branch</option>
                <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="!selectedBranchId" class="p-4 text-center text-muted">
          Select a branch to view the order list.
        </div>
        <div v-else-if="ordersLoading" class="p-4 text-center text-muted">Loading orders...</div>
        <div v-else-if="!orders.length" class="p-4 text-center text-muted">
          {{ statusFilter ? 'No orders with this status.' : 'No orders yet.' }}
        </div>
        <div v-else>
          <table class="table table-sm table-flush table-striped">
            <thead class="thead-light">
              <tr>
                <th>Created</th>
                <th>Branch</th>
                <th>Supplier</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th class="text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="order in orders">
                <tr :key="order.id">
                  <td>{{ formatWhen(order.created_at) }}</td>
                  <td>
                    <span
                      v-if="order.branch_name"
                      class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                      :style="branchBadgeStyle(branchColorFor(order.branch_id, order.branch_name))"
                    >{{ order.branch_name }}</span>
                    <span v-else>—</span>
                  </td>
                  <td>{{ order.supplier_name || '—' }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 font-weight-bold"
                      @click="openOrderItems(order)"
                    >
                      {{ order.item_count }}
                    </button>
                  </td>
                  <td>{{ money(order.total_amount) }}</td>
                  <td><span class="badge" :class="statusClass(order.status)">{{ statusLabel(order.status) }}</span></td>
                  <td class="text-left">
                    <div class="relative inline-block text-left">
                      <button
                        type="button"
                        class="inline-flex justify-center w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        @click.stop="toggleOrderMenu(order)"
                      >
                        Actions
                        <svg
                          class="w-4 h-4 ml-2 -mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                      <div
                        v-if="actionOrderId === order.id"
                        class="absolute right-0 z-10 w-48 mt-2 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                      >
                        <div class="py-1">
                          <button
                            type="button"
                            class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                            @click="toggleOrder(order)"
                          >
                            {{ openOrder && openOrder.id === order.id ? 'Hide' : 'View' }}
                          </button>
                          <button
                            type="button"
                            class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                            @click="openWhatsapp(order)"
                          >
                            Send to WhatsApp
                          </button>
                          <button
                            v-if="order.status !== 'completed'"
                            type="button"
                            class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                            :disabled="updatingOrderId === order.id"
                            @click="updateStatus(order, 'completed')"
                          >
                            Mark completed
                          </button>
                          <button
                            v-if="order.status !== 'pending'"
                            type="button"
                            class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                            :disabled="updatingOrderId === order.id"
                            @click="updateStatus(order, 'pending')"
                          >
                            Mark pending
                          </button>
                          <button
                            v-if="order.status !== 'cancelled'"
                            type="button"
                            class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                            :disabled="updatingOrderId === order.id"
                            @click="updateStatus(order, 'cancelled')"
                          >
                            Cancel order
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="openOrder && openOrder.id === order.id" :key="order.id + '-items'">
                  <td colspan="7" class="bg-white">
                    <div v-if="orderDetailLoading" class="py-2 text-muted">Loading items...</div>
                    <table v-else class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Barcode</th>
                          <th>Quantity</th>
                          <th>Unit price</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in openOrder.items" :key="item.id">
                          <td>{{ item.product_name || '—' }}</td>
                          <td>{{ item.barcode || '—' }}</td>
                          <td>{{ formatQuantity(item.quantity) }}</td>
                          <td>{{ money(item.unit_price) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <div v-if="ordersMeta" class="card-footer">
            <div class="flex-wrap col-12 d-flex justify-content-center justify-content-sm-between">
              <p class="card-category">Showing {{ ordersMeta.from }} to {{ ordersMeta.to }} of {{ ordersMeta.total }} entries</p>
              <pagination :meta="ordersMeta" @paginate="onOrdersPaginate" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal :show.sync="itemsModalShow" @close="clearOrderItems">
      <template #header>
        <h3 class="modal-title mb-0">Order Items</h3>
      </template>

      <div v-if="itemsModalLoading" class="py-4 text-center text-muted">Loading items...</div>
      <div v-else-if="!itemsOrder" class="py-4 text-center text-muted">Could not load this order.</div>
      <div v-else class="order-items">
        <p v-if="itemsOrder.supplier && itemsOrder.supplier.name" class="order-items-supplier mb-3">
          {{ itemsOrder.supplier.name }}
        </p>
        <div class="table-responsive">
          <table class="table table-sm mb-0 order-items-table">
            <thead class="thead-light">
              <tr>
                <th>Product</th>
                <th class="qty">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!(itemsOrder.items && itemsOrder.items.length)">
                <td colspan="2" class="text-center text-muted">No items on this order.</td>
              </tr>
              <tr v-for="item in itemsOrder.items" :key="item.id">
                <td>{{ item.product_name || '—' }}</td>
                <td class="qty">{{ formatQuantity(item.quantity) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template #footer>
        <button type="button" class="btn btn-secondary" @click="closeOrderItems">Close</button>
      </template>
    </modal>

    <modal :show.sync="whatsappModalShow" @close="clearWhatsapp">
      <template #header>
        <h3 class="modal-title mb-0">Send to WhatsApp</h3>
      </template>

      <div v-if="whatsappLoading" class="py-4 text-center text-muted">Loading order...</div>
      <div v-else>
        <p v-if="whatsappOrder && whatsappOrder.supplier" class="text-muted mb-2">
          {{ whatsappOrder.supplier.name }}
        </p>
        <label class="form-control-label" for="order-whatsapp-phone">Supplier phone</label>
        <input
          id="order-whatsapp-phone"
          v-model="whatsappPhone"
          type="text"
          class="form-control"
          placeholder="10-digit mobile number"
        />
        <p v-if="supplierPhoneInvalid" class="text-danger small mb-0 mt-2">
          The phone saved for this supplier is not a 10-digit number.
        </p>
      </div>

      <template #footer>
        <button type="button" class="btn btn-secondary" @click="closeWhatsapp">Close</button>
        <button
          type="button"
          class="btn btn-success"
          :disabled="whatsappSending || whatsappLoading || !whatsappOrder"
          @click="sendWhatsapp"
        >
          {{ whatsappSending ? 'Sending...' : 'Send to WhatsApp' }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import Modal from "@/components/Modal.vue";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";

const LARAVEL = endpoints.LARAVEL_BASE_URL;

export default {
  components: {
    "header-component": headerComponent,
    pagination,
    modal: Modal,
  },
  data() {
    return {
      branches: [],
      selectedBranchId: "",
      orders: [],
      ordersMeta: null,
      ordersPage: 1,
      ordersLoading: false,
      statusFilter: "",
      updatingOrderId: null,
      openOrder: null,
      actionOrderId: null,
      orderDetailLoading: false,
      itemsModalShow: false,
      itemsModalLoading: false,
      itemsOrder: null,
      itemsOrderRequestId: null,
      whatsappModalShow: false,
      whatsappLoading: false,
      whatsappSending: false,
      whatsappOrder: null,
      whatsappPhone: "",
      supplierPhoneInvalid: false,
      whatsappRequestId: null,
    };
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    if (this.$route.query.branch_id) {
      this.selectedBranchId = this.$route.query.branch_id;
      this.fetchOrders();
    }

    apiHelper.call("get", LARAVEL + "/api/v1/branches")
      .then(res => { this.branches = res.data.data || res.data; })
      .catch(() => {});

    apiHelper.call("get", LARAVEL + "/api/v1/me")
      .then(res => {
        if (this.selectedBranchId) return;
        if (res.data && res.data.current_branch_id) {
          this.selectedBranchId = res.data.current_branch_id;
          this.fetchOrders();
        }
      })
      .catch(() => {});
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (!event.target.closest(".relative")) {
        this.actionOrderId = null;
      }
    },
    onBranchChange() {
      this.ordersPage = 1;
      this.openOrder = null;
      this.fetchOrders();
    },
    onStatusChange() {
      this.ordersPage = 1;
      this.openOrder = null;
      this.fetchOrders();
    },
    fetchOrders() {
      if (!this.selectedBranchId) return;
      this.ordersLoading = true;
      const params = new URLSearchParams({
        branch_id: this.selectedBranchId,
        page: this.ordersPage,
        per_page: 20,
      });
      if (this.statusFilter) params.append("status", this.statusFilter);
      apiHelper.call("get", LARAVEL + "/api/v1/orders?" + params.toString())
        .then(res => {
          this.orders = res.data.orders || [];
          this.ordersMeta = res.data.meta || null;
        })
        .catch(() => {})
        .finally(() => { this.ordersLoading = false; });
    },
    onOrdersPaginate(data) {
      this.ordersPage = data.page;
      this.openOrder = null;
      this.fetchOrders();
    },
    toggleOrderMenu(order) {
      this.actionOrderId = this.actionOrderId === order.id ? null : order.id;
    },
    openOrderItems(order) {
      const requestedId = order.id;
      this.itemsOrderRequestId = requestedId;
      this.itemsOrder = null;
      this.itemsModalShow = true;
      this.itemsModalLoading = true;
      apiHelper.call("get", LARAVEL + "/api/v1/orders/" + requestedId)
        .then(res => {
          if (this.itemsOrderRequestId !== requestedId) return;
          this.itemsOrder = res.data.order || null;
        })
        .catch(() => {
          if (this.itemsOrderRequestId !== requestedId) return;
          this.itemsOrder = null;
        })
        .finally(() => {
          if (this.itemsOrderRequestId === requestedId) this.itemsModalLoading = false;
        });
    },
    openWhatsapp(order) {
      const requestedId = order.id;
      this.actionOrderId = null;
      this.whatsappRequestId = requestedId;
      this.whatsappOrder = null;
      this.whatsappPhone = "";
      this.supplierPhoneInvalid = false;
      this.whatsappModalShow = true;
      this.whatsappLoading = true;
      apiHelper.call("get", LARAVEL + "/api/v1/orders/" + requestedId)
        .then(res => {
          if (this.whatsappRequestId !== requestedId) return;
          this.whatsappOrder = res.data.order || null;
          const supplier = this.whatsappOrder && this.whatsappOrder.supplier;
          const savedPhone = supplier && supplier.phone ? String(supplier.phone) : "";
          this.whatsappPhone = this.usablePhone(savedPhone);
          this.supplierPhoneInvalid = !!savedPhone && !this.whatsappPhone;
        })
        .catch(() => {
          if (this.whatsappRequestId !== requestedId) return;
          this.whatsappOrder = null;
          this.notify("Could not load this order.", "danger");
        })
        .finally(() => {
          if (this.whatsappRequestId === requestedId) this.whatsappLoading = false;
        });
    },
    closeWhatsapp() {
      this.whatsappModalShow = false;
      this.clearWhatsapp();
    },
    clearWhatsapp() {
      this.whatsappOrder = null;
      this.whatsappPhone = "";
      this.supplierPhoneInvalid = false;
      this.whatsappRequestId = null;
      this.whatsappLoading = false;
      this.whatsappSending = false;
    },
    sendWhatsapp() {
      if (!this.whatsappOrder || this.whatsappSending) return;
      const phone = String(this.whatsappPhone || "").replace(/\D/g, "");
      const localPhone = phone.length > 10 ? phone.slice(-10) : phone;
      if (localPhone.length !== 10) {
        this.notify("The supplier phone must be a 10-digit mobile number.", "danger");
        return;
      }
      const orderId = this.whatsappOrder.id;
      this.whatsappSending = true;
      apiHelper.call("post", LARAVEL + "/api/v1/orders/" + orderId + "/whatsapp", {
        phone: localPhone,
      })
        .then(() => {
          this.notify("Order sent to WhatsApp.", "success");
          this.closeWhatsapp();
        })
        .catch(err => {
          const status = err.response && err.response.status;
          if (status === 401 || status === 503) return;
          this.notify(this.orderErrorMessage(err), "danger");
        })
        .finally(() => { this.whatsappSending = false; });
    },
    closeOrderItems() {
      this.itemsModalShow = false;
      this.clearOrderItems();
    },
    clearOrderItems() {
      this.itemsOrder = null;
      this.itemsOrderRequestId = null;
      this.itemsModalLoading = false;
    },
    usablePhone(value) {
      const digits = String(value || "").replace(/\D/g, "");
      const local = digits.length > 10 ? digits.slice(-10) : digits;
      return local.length === 10 ? local : "";
    },
    formatQuantity(value) {
      const amount = Number(value);
      if (Number.isNaN(amount)) return value || "—";
      return amount.toLocaleString("en-IN", { maximumFractionDigits: 3 });
    },
    money(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return "—";
      return amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    statusLabel(status) {
      if (status === "completed") return "Completed";
      if (status === "cancelled") return "Cancelled";
      if (status === "pending") return "Pending";
      return status || "—";
    },
    updateStatus(order, status) {
      if (!order || this.updatingOrderId) return;
      if (status === "cancelled" && !window.confirm("Cancel this order?")) {
        this.actionOrderId = null;
        return;
      }
      this.actionOrderId = null;
      this.updatingOrderId = order.id;
      apiHelper.call("patch", LARAVEL + "/api/v1/orders/" + order.id, { status })
        .then(res => {
          const updated = res.data.order;
          this.notify("Order updated.", "success");
          if (this.openOrder && this.openOrder.id === order.id && updated) {
            this.openOrder = Object.assign({}, this.openOrder, { status: updated.status });
          }
          if (this.statusFilter && updated && updated.status !== this.statusFilter) {
            this.fetchOrders();
            return;
          }
          const index = this.orders.findIndex(row => row.id === order.id);
          if (index !== -1 && updated) {
            this.$set(this.orders, index, Object.assign({}, this.orders[index], {
              status: updated.status,
            }));
          }
        })
        .catch(err => {
          const responseStatus = err.response && err.response.status;
          if (responseStatus === 401 || responseStatus === 503) return;
          this.notify(this.orderErrorMessage(err), "danger");
        })
        .finally(() => { this.updatingOrderId = null; });
    },
    toggleOrder(order) {
      this.actionOrderId = null;
      if (this.openOrder && this.openOrder.id === order.id) {
        this.openOrder = null;
        return;
      }
      this.orderDetailLoading = true;
      this.openOrder = order;
      apiHelper.call("get", LARAVEL + "/api/v1/orders/" + order.id)
        .then(res => { this.openOrder = res.data.order; })
        .catch(() => { this.openOrder = null; })
        .finally(() => { this.orderDetailLoading = false; });
    },
    formatWhen(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}/${date.getFullYear()}`;
    },
    branchBadgeStyle(color) {
      if (!color) return { background: "#dbeafe", color: "#1e40af" };
      return { background: color, color: "#fff" };
    },
    branchColorFor(branchId, branchName) {
      const match = this.branches.find(branch =>
        (branchId && String(branch.id) === String(branchId)) ||
        (branchName && branch.name === branchName)
      );
      return match ? match.color : null;
    },
    statusClass(status) {
      if (status === "completed") return "badge-success";
      if (status === "cancelled") return "badge-danger";
      return "badge-warning";
    },
    notify(message, type) {
      if (window.notify) {
        window.notify({ message, type, title: type === "success" ? "Success" : "Error" });
      }
    },
    orderErrorMessage(err) {
      const data = err && err.response && err.response.data;
      if (data && data.errors) {
        const first = Object.values(data.errors)[0];
        if (Array.isArray(first) && first.length) return first[0];
        if (typeof first === "string") return first;
      }
      if (data && data.message) return data.message;
      return "Could not complete this request.";
    },
  },
};
</script>

<style scoped>
.order-items-supplier {
  margin-bottom: 0.75rem;
  color: #525f7f;
  font-size: 0.875rem;
  font-weight: 600;
}

.order-items-table th,
.order-items-table td {
  vertical-align: middle;
}

.order-items-table th.qty,
.order-items-table td.qty {
  width: 7rem;
  text-align: right;
  white-space: nowrap;
}
</style>
